/**
 * Module: Book
 * Parent Block(s):
 * - list
 */

/**
 * External dependencies
 */

import isEmpty from 'lodash/isEmpty';

/**
 * DWT dependencies
 */

import { handleAudioContributors, cleanAudioContributors } from '../../components/utils/component-utils';
import { getBlockAttrs, getBlockParentsByName } from '../utils/block-utils';

import infoIcon from '../../img/infoIcon.svg';
import playButton from '../../img/playButton.svg';
import shareIcon from '../../img/shareIcon.svg';

/**
 * DWT components
 */

import Placeholder from '../block-components/Placeholder';

/**
 * WP dependencies
 */

import apiFetch from '@wordpress/api-fetch';

import {
  InspectorControls,
  MediaUpload,
  MediaUploadCheck,
  useBlockProps,
} from '@wordpress/block-editor';

import {
  Button,
  PanelBody,
  TextControl,
} from '@wordpress/components';

import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const Edit = ({ attributes, setAttributes, clientId }) => {
  const {
    apiImage,
    isbn,
    title,
    author,
    audioContributors,
    format,
    division,
    imprint,
    categories,
    awardWinner,
    bestseller,
    description,
    workId,
    showMetadata,
    allowedMetadataFields,
    allowedContributorRoles,
    uploadedImage,
    altTextManual,
    audioUrl,
  } = attributes;

  const listBlock = getBlockParentsByName( clientId, 'dwt/product-list' );
  const parentBlockAttrs = getBlockAttrs( listBlock.toString() );

  setAttributes({ showMetadata: parentBlockAttrs?.showMetadata });
  setAttributes({ allowedMetadataFields: parentBlockAttrs?.allowedMetadataFields });
  setAttributes({ allowedContributorRoles: parentBlockAttrs?.allowedContributorRoles });

  const isEmptyImage = isEmpty( uploadedImage );

  const blockProps = useBlockProps({
    className: `block__book`,
  });

  /**
   * Image settings
   */

  const ALLOWED_MEDIA_TYPES = [ 'image' ];
  const onSelectUploadedImage = ( newUploadedImage ) => setAttributes({ uploadedImage: newUploadedImage });

  const onRemoveUploadedImage = () => {
    setAttributes({
      uploadedImage: undefined,
      imageSelection: undefined,
    });
  };

  const uploadText = ! isEmptyImage ? 'Replace image' : 'Upload image';
  const onChangeAltTextManul = ( newAltTextManual ) => setAttributes({ altTextManual: newAltTextManual });

  /**
   * API image settings
   */

  const onChangeIsbn = ( newIsbn ) => setAttributes({ isbn: newIsbn });

  const [ isError, setError ] = useState( false );
  const [ data, setData ] = useState( [] );

  const onFetchApiImage = async() => {
    await apiFetch({
      path: '/dwt/v2/single-title',
      method: 'POST',
      data: { isbn },
    })
      .then( async( response ) => {
        if ( isError ) {
          setError( false );
        }
        const fetchAudioMP3 = await onFetchAudioMP3();
        response.data.audioUrl = fetchAudioMP3;
        setData( response.data );
      })
      .catch( ( error ) => {
        console.error( error );
        setError( true );
      });
  };

  const onFetchAudioMP3 = async() => {
    return await apiFetch({
      path: `/dwt/v2/audio-mp3`,
      method: 'POST',
      data: {
        asset_type: 18, // this is the default type for audio mp3 in the prh assets bucket
        isbn
      },
    })
      .then( ( response ) => {
        const result = response?.result[0]?.asset_types[0]?.assets[0]?.access?.url; // traverse down the response object to get the url
        return result;
      })
      .catch( ( error ) => {
        console.log({ error });
        setError( true );
      });
  };

  const onRemoveApiImage = () => {
    setAttributes({
      apiImage: undefined,
      title: undefined,
      author: undefined,
      isbn: undefined,
      uploadedImage: undefined,
    });
  };

  const getFormat = ( bookData ) => {
    let bookFormat;

    for ( const [ key, value ] of Object.entries( bookData.formats ) ) {
      const isbns = Object.keys( value );
      if ( isbns.includes( isbn ) ) {
        bookFormat = key;
      }
    }

    return bookFormat;
  };

  useEffect( () => {
    if ( isEmpty( data ) ) {
      return;
    }
    const encodedTitle = encodeURIComponent( data.title );
    const encodedAuthor = encodeURIComponent( data.frontlistiestTitle?.author );
    const encodedDescription = encodeURIComponent( data.frontlistiestTitle?.aboutTheBook );
    const audioContributors = cleanAudioContributors( data?.formats?.Audio?.[ isbn ]?.contributors );

    setAttributes({
      title: encodedTitle,
      author: encodedAuthor,
      audioContributors,
      format: getFormat( data ),
      awardWinner: ! isEmpty( data?.bookAwards ),
      bestseller: data.frontlistiestTitle?.bestseller,
      division: data.frontlistiestTitle?.division.description,
      imprint: data.frontlistiestTitle?.imprint.name,
      categories: data.frontlistiestTitle?.categories,
      description: encodedDescription,
      workId: data?.workId,
      apiImage: isbn ? `https://images.penguinrandomhouse.com/cover/${ isbn }` : undefined,
      audioUrl: data?.audioUrl,
    });
  }, [ data ] );

  /**
   * Handle additional logic
   */

  const inValidHelpText = isError ? 'Please enter a valid ISBN' : 'Required';
  const showPlaceholder = !!( isEmptyImage && ! apiImage );

  const apiImageAltText = `${ title } by ${ author }`;
  const imageSrc = ! isEmptyImage ? uploadedImage.sizes.full.url : apiImage;
  const imageAltText = altTextManual || ( altTextManual === '' || ! altTextManual ? apiImageAltText : uploadedImage.alt );

  const getAudioContributors = handleAudioContributors( audioContributors, false );

  const bookCoverStyle = window?.DP?.book_cover_background;
  const isAudio = bookCoverStyle === 'Audio';
  /**
   * Handle element styles
   */

  /**
   * Handle element classes
   */

  return (
    <>
      <InspectorControls>
        <PanelBody>
          { ! apiImage && (
            <>
              <TextControl
                className={ isError ? 'has-error' : ''}
                label={ __( 'ISBN', 'dwt-custom-theme' ) }
                value= { isbn }
                onChange={ onChangeIsbn }
                help={ __( inValidHelpText, 'dwt-custom-theme' ) }
              />
              <Button
                onClick={ onFetchApiImage }
                className='editor-media-placeholder__button is-button is-default is-large is-secondary rounded is-button-fetch'
              >
                { __( 'Fetch book', 'dwt-custom-theme' ) }
              </Button>
            </>
          ) }
          { apiImage && (
            <>
              <div className="isbn mb-24">
                <h2>{ decodeURIComponent( title ) }</h2>
                <h2>ISBN</h2>
                <p className="mb-5">{ isbn }</p>
                <Button
                  onClick={ onRemoveApiImage }
                  isLink
                  isDestructive
                  className='flex mb-10'
                >
                  { __( 'Remove ISBN', 'dwt-custom-theme' ) }
                </Button>
              </div>

              <div className="components-base-control">
                <MediaUploadCheck>
                  <p className="mb-10">Use an uploaded image.</p>
                  <MediaUpload
                    allowedTypes={ ALLOWED_MEDIA_TYPES }
                    onSelect={ onSelectUploadedImage }
                    type='image'
                    value={ uploadedImage }
                    render={ ({ open }) => (
                      <Button
                        onClick={ open }
                        icon='upload'
                        className='editor-media-placeholder__button is-button is-default is-large is-secondary is-upload-button'
                      >
                        { __( uploadText, 'dwt-custom-theme' ) }
                      </Button>
                    ) }
                  />
                </MediaUploadCheck>
                { ! isEmptyImage && (
                  <>
                    <Button
                      onClick={ onRemoveUploadedImage }
                      isLink
                      isDestructive
                      className='is-remove-button'
                    >
                      { __( 'Remove uploaded image', 'dwt-custom-theme' ) }
                    </Button>
                    <p className="text-12 text-gray">Removing uploaded image defaults back to image from the API</p>
                  </>
                ) }
              </div>
              <TextControl
                label={ __( 'Alt Text', 'dwt-custom-theme' ) }
                value= { altTextManual }
                onChange={ onChangeAltTextManul }
                help={ __( 'Add alt text', 'dwt-custom-theme' ) }
              />
            </>
          ) }
        </PanelBody>

      </InspectorControls>

      <div { ...blockProps }>
        <div className={`book--inner ${!isAudio ? 'with-hover' : ''}`}>
          { showPlaceholder ? (

            <Placeholder
              title={ __( 'Image', 'dwt-custom-theme' ) }
              text={ __( 'Enter an ISBN from the side panel.', 'dwt-custom-theme' ) }
              icon='format-image'
            />

          ) : (
            <>
              { isAudio ? (
                <div className="audio-book">
                  <div className="image-wrapper">
                    <div className="api-image-wrapper">
                      <img className='api-image' src={ imageSrc } alt={ imageAltText } data-format={ format } data-isbn={ isbn } data-workid={ workId } loading="lazy" />
                    </div>
                    <div className="icon-wrapper">
                      <img className="icon" src={infoIcon} />
                      { audioUrl && (
                        <img className="icon" src={playButton} />
                      )}
                      <img className="icon" src={shareIcon} />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="image-wrapper bg-light-gray aspect-[81/100]">
                    <div className="api-image-wrapper w-full">
                      <img className='api-image shadow-book-img w-[60%]' src={ imageSrc } alt={ imageAltText } data-format={ format } data-isbn={ isbn } data-workid={ workId } loading="lazy" />
                    </div>
                  </div>
                </>
              )}


              { showMetadata && (
                <div className="book-details pt-16 w-full">
                  { title && allowedMetadataFields?.includes( 'Title' ) && (
                    <p className="book-details__title paragraph-1 text-16 mt-0 mb-0">{ decodeURIComponent( title ) }</p>
                  ) }


                  { allowedMetadataFields?.includes( 'Contributor Name' ) && (
                    <>
                      { isAudio ? (
                        <p className="book-details__author paragraph-2 text-[0.9rem] my-0">{ getAudioContributors || decodeURIComponent( author ) }</p>
                      ) : (
                        <p className="book-details__author paragraph-2 text-[0.9rem] mt-[0.1rem] my-0">{ decodeURIComponent( author ) }</p>
                      )}
                    </>
                  )}

                  { format && allowedMetadataFields?.includes( 'Format' ) && (
                    <p className="book-details__format paragraph-2 text-[0.9rem] mt-[0.1rem] mb-0 text-[#606060]">{ format }</p>
                  ) }
                </div>
              ) }
            </>
          )}

        </div>

      </div>
    </>
  );
};

export default Edit;
